import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle, Archive, ArrowLeft, BadgeCheck, CheckCircle2, ChevronRight, FileClock, Scale,
  Clock3, KeyRound, Loader2, Lock, Mail, MessageSquare, Palette, Pencil, RefreshCw, Save, Search,
  ShieldCheck, ShieldOff, Trash2, Unlock, UserCheck, UserPlus, Users, X
} from 'lucide-react'
import { api } from '@/lib/api'
import { CapabilityControl } from './CapabilityControl'

interface StaffMember {
  user_id: string
  email: string
  display_name: string | null
  role: string
  status: string
  invited_by?: string | null
  invited_at: string | null
  invitation_expires_at?: string | null
  invitation_accepted_at?: string | null
  email_verified_at: string | null
  profile_completed_at: string | null
  created_at?: string | null
  updated_at?: string | null
  first_name: string | null
  middle_name?: string | null
  last_name: string | null
  job_title: string | null
  department: string | null
  phone?: string | null
  timezone?: string | null
  locale?: string | null
  avatar_url: string | null
  archived?: boolean
  archive_id?: string | null
  employment_ended_at?: string | null
  archived_at?: string | null
  departure_reason?: string | null
  retention_review_at?: string | null
  legal_hold?: boolean
  legal_hold_reason?: string | null
  erasure_status?: string | null
  erasure_due_at?: string | null
  erasure_requests?: Record<string, unknown>[]
  lifecycle_events?: Record<string, unknown>[]
}

type DirectoryFilter = 'all' | 'active' | 'onboarding' | 'attention' | 'archived'
type DetailTab = 'overview' | 'access' | 'security'
type StaffAction = 'lock' | 'unlock' | 'revoke' | 'delete_test' | 'resend_invite' | 'archive' | 'erasure_request' | 'legal_hold_set' | 'legal_hold_release'
type InviteRole = 'admin' | 'manager' | 'developer' | 'staff' | 'viewer'

interface InviteTemplateMeta {
  id: string
  name: string
  description: string
  tone: string
  roles: InviteRole[]
  accent: string
  surface: string
  eyebrow: string
}

interface InviteTemplateLibrary {
  templates: InviteTemplateMeta[]
  defaults_by_role: Record<InviteRole, string>
}

const ONBOARDING = new Set(['invited', 'email_verified', 'totp_pending', 'profile_pending'])
const ATTENTION = new Set(['locked', 'revoked'])

const STATUS: Record<string, { label: string; tone: string; dot: string }> = {
  invited: { label: 'Invited', tone: 'bg-slate-500/10 text-slate-500 border-slate-500/20', dot: 'bg-slate-400' },
  email_verified: { label: 'Email verified', tone: 'bg-blue-500/10 text-blue-500 border-blue-500/20', dot: 'bg-blue-400' },
  totp_pending: { label: 'Authenticator setup', tone: 'bg-amber-500/10 text-amber-600 border-amber-500/20', dot: 'bg-amber-400' },
  profile_pending: { label: 'Profile setup', tone: 'bg-amber-500/10 text-amber-600 border-amber-500/20', dot: 'bg-amber-400' },
  active: { label: 'Active', tone: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', dot: 'bg-emerald-400' },
  locked: { label: 'Locked', tone: 'bg-red-500/10 text-red-500 border-red-500/20', dot: 'bg-red-400' },
  revoked: { label: 'Revoked', tone: 'bg-red-900/10 text-red-600 border-red-500/20', dot: 'bg-red-500' },
  archived: { label: 'Former staff', tone: 'bg-slate-500/10 text-slate-600 border-slate-500/20', dot: 'bg-slate-500' },
}

const ROLE_COPY: Record<InviteRole, { label: string; description: string }> = {
  admin: { label: 'Admin', description: 'Administrative staff identity.' },
  manager: { label: 'Manager', description: 'Operational management identity.' },
  developer: { label: 'Developer', description: 'Engineering and development identity.' },
  staff: { label: 'Staff', description: 'General staff identity.' },
  viewer: { label: 'Viewer', description: 'Read-focused staff identity.' },
}

function roleLabel(role: string | null | undefined) {
  if (!role) return 'Unassigned'
  const value = role.toLowerCase() as InviteRole
  return ROLE_COPY[value]?.label || role.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function statusConfig(status: string) {
  return STATUS[status] || { label: status || 'Unknown', tone: 'bg-slate-500/10 text-slate-500 border-slate-500/20', dot: 'bg-slate-400' }
}

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig(status)
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${config.tone}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  )
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-accent/15 bg-accent/8 px-2.5 py-1 text-[11px] font-medium text-accent">
      {roleLabel(role)}
    </span>
  )
}

function Avatar({ member, size = 48 }: { member: Pick<StaffMember, 'display_name' | 'email' | 'avatar_url'>; size?: number }) {
  const name = member.display_name || member.email
  if (member.avatar_url) {
    return <img src={member.avatar_url} alt="" className="shrink-0 rounded-2xl object-cover ring-1 ring-border" style={{ width: size, height: size }} />
  }
  const initials = name.split(/[\s@]+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div
      className="shrink-0 rounded-2xl border border-accent/15 bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center font-semibold text-accent shadow-sm"
      style={{ width: size, height: size, fontSize: Math.max(13, size * 0.3) }}
    >
      {initials || '?'}
    </div>
  )
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
      <div className="break-words text-sm text-text-primary">{value || 'Not provided'}</div>
    </div>
  )
}

function displayDate(value?: string | null) {
  if (!value) return 'Not yet'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

function mergeDetail(
  data: { user: Record<string, unknown>; profile: Record<string, unknown> | null },
  fallback?: StaffMember | null,
): StaffMember {
  const user = data.user || {}
  const profile = data.profile || {}
  const stringOrNull = (value: unknown) => typeof value === 'string' ? value : null
  return {
    user_id: stringOrNull(user.user_id) || fallback?.user_id || '',
    email: stringOrNull(user.email) || fallback?.email || '',
    display_name: stringOrNull(profile.display_name) || stringOrNull(user.display_name) || fallback?.display_name || null,
    role: stringOrNull(user.role) || fallback?.role || 'staff',
    status: stringOrNull(user.status) || fallback?.status || 'invited',
    invited_by: stringOrNull(user.invited_by),
    invited_at: stringOrNull(user.invited_at) || fallback?.invited_at || null,
    invitation_expires_at: stringOrNull(user.invitation_expires_at),
    invitation_accepted_at: stringOrNull(user.invitation_accepted_at),
    email_verified_at: stringOrNull(user.email_verified_at) || fallback?.email_verified_at || null,
    profile_completed_at: stringOrNull(profile.profile_completed_at) || stringOrNull(user.profile_completed_at) || fallback?.profile_completed_at || null,
    created_at: stringOrNull(user.created_at),
    updated_at: stringOrNull(user.updated_at),
    first_name: stringOrNull(profile.first_name) || fallback?.first_name || null,
    middle_name: stringOrNull(profile.middle_name),
    last_name: stringOrNull(profile.last_name) || fallback?.last_name || null,
    job_title: stringOrNull(profile.job_title) || fallback?.job_title || null,
    department: stringOrNull(profile.department) || fallback?.department || null,
    phone: stringOrNull(profile.phone),
    timezone: stringOrNull(profile.timezone),
    locale: stringOrNull(profile.locale),
    avatar_url: stringOrNull(profile.avatar_url) || fallback?.avatar_url || null,
  }
}

function formerStaffFromRow(row: Record<string, unknown>): StaffMember {
  const str = (value: unknown) => typeof value === 'string' ? value : null
  return {
    user_id: str(row.user_id) || '',
    email: str(row.email) || '',
    display_name: str(row.display_name),
    role: str(row.role) || 'staff',
    status: 'archived',
    invited_at: null, email_verified_at: null, profile_completed_at: null,
    first_name: str(row.first_name), last_name: str(row.last_name), job_title: str(row.job_title), department: str(row.department), avatar_url: str(row.avatar_url),
    archived: true, archive_id: str(row.archive_id), employment_ended_at: str(row.employment_ended_at), archived_at: str(row.archived_at),
    departure_reason: str(row.departure_reason), retention_review_at: str(row.retention_review_at), legal_hold: row.legal_hold === 1 || row.legal_hold === true,
    erasure_status: str(row.erasure_status), erasure_due_at: str(row.erasure_due_at),
  }
}

function formerStaffFromDetail(data: { archive: Record<string, unknown>; erasure_requests: Record<string, unknown>[]; lifecycle_events: Record<string, unknown>[] }, fallback?: StaffMember | null): StaffMember {
  const member = formerStaffFromRow(data.archive)
  return { ...fallback, ...member, archived: true, legal_hold_reason: typeof data.archive.legal_hold_reason === 'string' ? data.archive.legal_hold_reason : null,
    phone: typeof data.archive.phone === 'string' ? data.archive.phone : fallback?.phone || null, timezone: typeof data.archive.timezone === 'string' ? data.archive.timezone : fallback?.timezone || null,
    locale: typeof data.archive.locale === 'string' ? data.archive.locale : fallback?.locale || null, erasure_requests: data.erasure_requests, lifecycle_events: data.lifecycle_events }
}

export function StaffPanel() {
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [formerStaff, setFormerStaff] = useState<StaffMember[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<DirectoryFilter>('all')

  const [selected, setSelected] = useState<StaffMember | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailTab, setDetailTab] = useState<DetailTab>('overview')
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const [showInvite, setShowInvite] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [inviteTemplates, setInviteTemplates] = useState<InviteTemplateLibrary | null>(null)
  const [inviteTemplatesLoading, setInviteTemplatesLoading] = useState(false)
  const [inviteForm, setInviteForm] = useState({
    role: 'developer' as InviteRole,
    template_id: '',
    personal_message: '',
    email: '',
    display_name: '',
    first_name: '',
    last_name: '',
    job_title: '',
    department: '',
    phone: '',
    timezone: 'Europe/London',
    locale: 'en-GB',
  })

  const [pendingAction, setPendingAction] = useState<StaffAction | null>(null)
  const [actionBusy, setActionBusy] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [archiveForm, setArchiveForm] = useState({ employment_ended_at: new Date().toISOString().slice(0, 10), departure_reason: 'resignation', departure_note: '' })
  const [erasureChannel, setErasureChannel] = useState<'email' | 'letter' | 'portal' | 'verbal' | 'other'>('email')
  const [erasureScope, setErasureScope] = useState('all personal data')
  const [legalHoldReason, setLegalHoldReason] = useState('')

  const loadStaff = useCallback(async (quiet = false) => {
    if (quiet) setRefreshing(true)
    else setLoading(true)
    setError('')
    try {
      const data = await api.listStaff() as StaffMember[]
      if (!Array.isArray(data)) throw new Error('Invalid staff directory response')
      setStaff(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Staff directory is unavailable.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  const loadFormerStaff = useCallback(async () => {
    try {
      const rows = await api.listFormerStaff()
      setFormerStaff(rows.map(row => formerStaffFromRow(row as Record<string, unknown>)))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Former staff archive is unavailable.')
    }
  }, [])

  const loadMember = useCallback(async (id: string, fallback?: StaffMember | null) => {
    setDetailLoading(true)
    setError('')
    try {
      if (fallback?.archived) {
        const detail = await api.getFormerStaffMember(id)
        setSelected(formerStaffFromDetail(detail, fallback))
      } else {
        const detail = await api.getStaffMember(id)
        setSelected(mergeDetail(detail, fallback))
      }
    } catch (e) {
      if (fallback) setSelected(fallback)
      setError(e instanceof Error ? e.message : 'Staff record is unavailable.')
    } finally {
      setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true
    Promise.all([api.listStaff(), api.listFormerStaff()]).then(([data, former]) => {
      if (!active) return
      if (!Array.isArray(data) || !Array.isArray(former)) throw new Error('Invalid staff directory response')
      setStaff(data as StaffMember[])
      setFormerStaff(former.map(row => formerStaffFromRow(row as Record<string, unknown>)))
      setError('')
    }).catch(error => {
      if (active) setError(error instanceof Error ? error.message : 'Staff directory is unavailable.')
    }).finally(() => {
      if (active) setLoading(false)
    })
    return () => { active = false }
  }, [])

  const metrics = useMemo(() => {
    const active = staff.filter(member => member.status === 'active').length
    const onboarding = staff.filter(member => ONBOARDING.has(member.status)).length
    const attention = staff.filter(member => ATTENTION.has(member.status)).length
    return { total: staff.length, active, onboarding, attention, archived: formerStaff.length }
  }, [staff, formerStaff])

  const compatibleInviteTemplates = useMemo(
    () => inviteTemplates?.templates.filter(template => template.roles.includes(inviteForm.role)) ?? [],
    [inviteTemplates, inviteForm.role],
  )

  const selectedInviteTemplate = useMemo(
    () => compatibleInviteTemplates.find(template => template.id === inviteForm.template_id) ?? null,
    [compatibleInviteTemplates, inviteForm.template_id],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const source = filter === 'archived' ? formerStaff : staff
    return source
      .filter(member => {
        if (filter === 'active' && member.status !== 'active') return false
        if (filter === 'onboarding' && !ONBOARDING.has(member.status)) return false
        if (filter === 'attention' && !ATTENTION.has(member.status)) return false
        if (filter === 'archived' && !member.archived) return false
        if (!q) return true
        return [
          member.display_name, member.email, member.first_name, member.last_name,
          member.job_title, member.department, member.role,
        ].some(value => (value || '').toLowerCase().includes(q))
      })
      .sort((a, b) => (a.display_name || a.email).localeCompare(b.display_name || b.email))
  }, [staff, formerStaff, query, filter])

  function beginEdit() {
    if (!selected) return
    setEditData({
      first_name: selected.first_name || '',
      middle_name: selected.middle_name || '',
      last_name: selected.last_name || '',
      display_name: selected.display_name || '',
      job_title: selected.job_title || '',
      department: selected.department || '',
      phone: selected.phone || '',
      timezone: selected.timezone || 'Europe/London',
      locale: selected.locale || 'en-GB',
    })
    setEditing(true)
  }

  async function saveProfile() {
    if (!selected) return
    setSaving(true)
    setError('')
    try {
      await api.updateStaffProfile(selected.user_id, editData)
      setEditing(false)
      setSuccess('Staff profile updated.')
      await Promise.all([loadStaff(true), loadMember(selected.user_id, selected)])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Profile update failed.')
    } finally {
      setSaving(false)
    }
  }

  async function openInviteModal() {
    setError('')
    setSuccess('')
    setShowInvite(true)
    if (inviteTemplates || inviteTemplatesLoading) return
    setInviteTemplatesLoading(true)
    try {
      const library = await api.inviteTemplates()
      setInviteTemplates(library)
      setInviteForm(current => ({
        ...current,
        template_id: library.defaults_by_role[current.role] || '',
      }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invitation templates are unavailable. OPS will use the role default.')
    } finally {
      setInviteTemplatesLoading(false)
    }
  }

  async function inviteStaff() {
    if (!inviteForm.email.trim()) return
    setInviting(true)
    setError('')
    setSuccess('')
    try {
      const displayName = inviteForm.display_name.trim()
        || [inviteForm.first_name.trim(), inviteForm.last_name.trim()].filter(Boolean).join(' ')
        || inviteForm.email.split('@')[0]
      const result = await api.inviteStaff({
        email: inviteForm.email.trim(),
        display_name: displayName,
        first_name: inviteForm.first_name,
        role: inviteForm.role,
        job_title: inviteForm.job_title,
        department: inviteForm.department,
        template_id: inviteForm.template_id || undefined,
        personal_message: inviteForm.personal_message,
      })
      await api.updateStaffProfile(result.user_id, {
        first_name: inviteForm.first_name,
        last_name: inviteForm.last_name,
        display_name: displayName,
        job_title: inviteForm.job_title,
        department: inviteForm.department,
        phone: inviteForm.phone,
        timezone: inviteForm.timezone,
        locale: inviteForm.locale,
      })
      setShowInvite(false)
      setSuccess(result.email_sent
        ? `Invitation sent to ${inviteForm.email.trim()}.`
        : `Staff account created for ${inviteForm.email.trim()}, but email delivery was not confirmed.`)
      setInviteForm({
        role: 'developer',
        template_id: inviteTemplates?.defaults_by_role.developer || '',
        personal_message: '',
        email: '',
        display_name: '',
        first_name: '',
        last_name: '',
        job_title: '',
        department: '',
        phone: '',
        timezone: 'Europe/London',
        locale: 'en-GB',
      })
      await loadStaff(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invitation failed.')
    } finally {
      setInviting(false)
    }
  }

  async function runAccountAction(action: StaffAction) {
    if (!selected) return
    setActionBusy(true)
    setError('')
    try {
      if (action === 'lock') await api.lockStaff(selected.user_id)
      if (action === 'unlock') await api.unlockStaff(selected.user_id)
      if (action === 'revoke') await api.revokeStaff(selected.user_id)
      if (action === 'archive') {
        const result = await api.archiveStaff(selected.user_id, {
          employment_ended_at: archiveForm.employment_ended_at,
          departure_reason: archiveForm.departure_reason as 'resignation' | 'dismissal' | 'redundancy' | 'end_of_contract' | 'retirement' | 'transfer' | 'other',
          departure_note: archiveForm.departure_note || undefined,
        })
        setPendingAction(null)
        setSelected(null)
        setFilter('archived')
        setSuccess(`Staff member archived. Retention review: ${displayDate(result.retention_review_at)}.`)
        await Promise.all([loadStaff(true), loadFormerStaff()])
        return
      }
      if (action === 'erasure_request') {
        const result = await api.recordStaffErasureRequest(selected.user_id, { request_channel: erasureChannel, scope: erasureScope })
        setPendingAction(null)
        setSuccess(`Erasure request recorded. Internal response target: ${displayDate(result.response_due_at)}.`)
        await Promise.all([loadFormerStaff(), loadMember(selected.user_id, { ...selected, archived: true })])
        return
      }
      if (action === 'legal_hold_set' || action === 'legal_hold_release') {
        const hold = action === 'legal_hold_set'
        await api.setStaffLegalHold(selected.user_id, hold, legalHoldReason)
        setPendingAction(null)
        setLegalHoldReason('')
        setSuccess(hold ? 'Legal hold applied.' : 'Legal hold released.')
        await Promise.all([loadFormerStaff(), loadMember(selected.user_id, { ...selected, archived: true })])
        return
      }
      if (action === 'resend_invite') {
        const result = await api.resendStaffInvitation(selected.user_id)
        setPendingAction(null)
        setSuccess(`Invitation resent. New invitation window expires ${displayDate(result.invitation_expires_at)}.`)
        await Promise.all([loadStaff(true), loadMember(selected.user_id, selected)])
        return
      }
      if (action === 'delete_test') {
        await api.deleteTestStaff(selected.user_id, deleteConfirm)
        setPendingAction(null)
        setDeleteConfirm('')
        setSelected(null)
        setSuccess('Test/incomplete account permanently deleted.')
        await loadStaff(true)
        return
      }
      setPendingAction(null)
      setDeleteConfirm('')
      setSuccess(action === 'lock' ? 'Account locked.' : action === 'unlock' ? 'Account unlocked.' : 'Account access revoked.')
      await Promise.all([loadStaff(true), loadMember(selected.user_id, selected)])
    } catch (e) {
      setError(e instanceof Error ? e.message : `${action} failed.`)
    } finally {
      setActionBusy(false)
    }
  }

  async function openMember(member: StaffMember) {
    setSuccess('')
    setEditing(false)
    setDetailTab('overview')
    setSelected(member)
    await loadMember(member.user_id, member)
  }

  if (selected) {
    const status = statusConfig(selected.status)
    return (
      <div className="mx-auto w-full max-w-6xl px-4 pb-10 pt-4 sm:px-6 sm:pt-6">
        <button
          onClick={() => { setSelected(null); setEditing(false); setPendingAction(null) }}
          className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-medium text-text-muted transition hover:bg-surface hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Staff Portal
        </button>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border border-red-500/15 bg-red-500/5 p-3 text-sm text-red-500">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-4 flex items-start gap-2 rounded-2xl border border-emerald-500/15 bg-emerald-500/5 p-3 text-sm text-emerald-600">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {success}
          </div>
        )}

        <section className="relative overflow-hidden rounded-[1.75rem] border border-border bg-surface/85 p-5 shadow-sm backdrop-blur-xl sm:p-6">
          <div className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <Avatar member={selected} size={64} />
              <div className="min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-semibold text-text-primary sm:text-2xl">
                    {selected.display_name || selected.email.split('@')[0]}
                  </h1>
                  {detailLoading && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
                </div>
                <p className="truncate text-sm text-text-muted">
                  {selected.job_title || 'Staff member'}{selected.department ? ` · ${selected.department}` : ''}
                </p>
                <p className="mt-1 truncate text-xs text-text-muted/80">{selected.email}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <StatusBadge status={selected.status} />
                  <RoleBadge role={selected.role} />
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-bg/50 px-4 py-3 sm:min-w-[210px]">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">{selected.archived ? 'Employment lifecycle' : 'Onboarding status'}</div>
              <div className="mt-1 flex items-center gap-2 text-sm font-medium text-text-primary">
                <span className={`h-2 w-2 rounded-full ${status.dot}`} />
                {status.label}
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-text-muted">
                {selected.archived ? 'This former staff identity is security-revoked. Retention and erasure are governed separately below.' : 'Account role is identity metadata. Effective permissions are verified separately under Access.'}
              </p>
            </div>
          </div>
        </section>

        <div className="mt-4 grid grid-cols-3 gap-1 rounded-2xl border border-border bg-surface/75 p-1 backdrop-blur-xl">
          {([
            ['overview', 'Overview', UserCheck],
            ['access', 'Access', ShieldCheck],
            ['security', 'Security', KeyRound],
          ] as const).map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setDetailTab(id)}
              className={`flex items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 text-xs font-medium transition sm:text-sm ${
                detailTab === id ? 'bg-accent text-white shadow-sm' : 'text-text-muted hover:bg-surface-elevated hover:text-text-primary'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {detailTab === 'overview' && (
          <div className="mt-4 grid gap-4 lg:grid-cols-[1.35fr_.65fr]">
            <section className="rounded-[1.5rem] border border-border bg-surface/80 p-5 backdrop-blur-xl">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Staff profile</div>
                  <h2 className="mt-1 text-lg font-semibold text-text-primary">Personal & work details</h2>
                </div>
                {!editing && !selected.archived && (
                  <button onClick={beginEdit} className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-bg/50 px-3 py-2 text-xs font-medium text-text-secondary transition hover:border-accent/30 hover:text-accent">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                )}
              </div>

              {editing ? (
                <div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {([
                      ['first_name', 'First name'], ['middle_name', 'Middle name'], ['last_name', 'Last name'],
                      ['display_name', 'Display name'], ['job_title', 'Job title'], ['department', 'Department'],
                      ['phone', 'Phone'], ['timezone', 'Timezone'], ['locale', 'Locale'],
                    ] as const).map(([key, label]) => (
                      <label key={key} className="block">
                        <span className="mb-1 block text-[11px] font-medium text-text-muted">{label}</span>
                        <input
                          value={editData[key] || ''}
                          onChange={event => setEditData(current => ({ ...current, [key]: event.target.value }))}
                          className="w-full rounded-xl border border-border bg-bg/60 px-3 py-2 text-sm text-text-primary outline-none transition focus:border-accent/40"
                        />
                      </label>
                    ))}
                  </div>
                  <div className="mt-5 flex gap-2">
                    <button
                      onClick={saveProfile}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
                    </button>
                    <button onClick={() => setEditing(false)} className="rounded-xl px-4 py-2 text-xs font-medium text-text-muted transition hover:bg-surface-elevated">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
                  <InfoField label="First name" value={selected.first_name} />
                  <InfoField label="Last name" value={selected.last_name} />
                  <InfoField label="Display name" value={selected.display_name} />
                  <InfoField label="Email" value={selected.email} />
                  <InfoField label="Phone" value={selected.phone} />
                  <InfoField label="Job title" value={selected.job_title} />
                  <InfoField label="Department" value={selected.department} />
                  <InfoField label="Timezone" value={selected.timezone} />
                  <InfoField label="Locale" value={selected.locale} />
                </div>
              )}
            </section>

            <section className="rounded-[1.5rem] border border-border bg-surface/80 p-5 backdrop-blur-xl">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Account journey</div>
              <h2 className="mt-1 text-lg font-semibold text-text-primary">{selected.archived ? 'Former staff record' : 'Onboarding'}</h2>
              {selected.archived && (
                <div className="mt-5 space-y-3 rounded-xl border border-border bg-bg/45 p-3">
                  <InfoField label="Employment ended" value={displayDate(selected.employment_ended_at)} />
                  <InfoField label="Archived" value={displayDate(selected.archived_at)} />
                  <InfoField label="Departure reason" value={selected.departure_reason?.replaceAll('_', ' ')} />
                  <InfoField label="Ordinary HR review point" value={displayDate(selected.retention_review_at)} />
                  <InfoField label="Legal hold" value={selected.legal_hold ? 'Active' : 'None'} />
                  <InfoField label="Latest erasure request" value={selected.erasure_status ? `${selected.erasure_status}${selected.erasure_due_at ? ` · due ${displayDate(selected.erasure_due_at)}` : ''}` : 'None'} />
                </div>
              )}
              <div className="mt-5 space-y-4">
                {[
                  ['Invitation sent', selected.invited_at, Mail],
                  ['Email verified', selected.email_verified_at, BadgeCheck],
                  ['Profile completed', selected.profile_completed_at, UserCheck],
                ].map(([label, value, Icon]) => {
                  const StepIcon = Icon as typeof Mail
                  const complete = !!value
                  return (
                    <div key={label as string} className="flex gap-3">
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${
                        complete ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-600' : 'border-border bg-bg/50 text-text-muted'
                      }`}>
                        <StepIcon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-text-primary">{label as string}</div>
                        <div className="mt-0.5 text-[11px] text-text-muted">{displayDate(value as string | null)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="mt-5 rounded-xl border border-border bg-bg/45 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">Account role</div>
                <div className="mt-1 text-sm font-semibold text-text-primary">{roleLabel(selected.role)}</div>
                <p className="mt-1 text-[11px] leading-relaxed text-text-muted">Role identity and effective capability authority are intentionally shown separately.</p>
              </div>
            </section>
          </div>
        )}

        {detailTab === 'access' && (
          <section className="mt-4 rounded-[1.5rem] border border-border bg-surface/80 p-4 backdrop-blur-xl sm:p-5">
            <div className="mb-4 rounded-2xl border border-accent/10 bg-accent/5 p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <div>
                  <h2 className="text-sm font-semibold text-text-primary">Effective access</h2>
                  <p className="mt-1 text-xs leading-relaxed text-text-muted">
                    This is the authoritative capability result for this person. Their account role above does not independently grant execution authority.
                  </p>
                </div>
              </div>
            </div>
            {selected.archived ? (
              <div className="rounded-2xl border border-slate-500/15 bg-slate-500/5 p-4">
                <div className="flex items-start gap-3">
                  <Archive className="mt-0.5 h-5 w-5 text-slate-500" />
                  <div><div className="text-sm font-semibold text-text-primary">Execution access revoked</div><p className="mt-1 text-xs leading-relaxed text-text-muted">Former staff cannot authenticate or execute OPS operations. Historical capability records remain evidence and are not editable from the archive.</p></div>
                </div>
              </div>
            ) : <CapabilityControl key={selected.user_id} principalId={selected.user_id} />}
          </section>
        )}

        {detailTab === 'security' && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <section className="rounded-[1.5rem] border border-border bg-surface/80 p-5 backdrop-blur-xl">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Security state</div>
              <h2 className="mt-1 text-lg font-semibold text-text-primary">Authentication</h2>
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-bg/45 p-3">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-text-muted" />
                    <div><div className="text-sm font-medium text-text-primary">Email verification</div><div className="text-[11px] text-text-muted">{selected.email}</div></div>
                  </div>
                  <span className={selected.email_verified_at ? 'text-xs font-semibold text-emerald-600' : 'text-xs font-semibold text-amber-600'}>
                    {selected.email_verified_at ? 'Verified' : 'Pending'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-bg/45 p-3">
                  <div className="flex items-center gap-3">
                    <KeyRound className="h-4 w-4 text-text-muted" />
                    <div><div className="text-sm font-medium text-text-primary">Authenticator</div><div className="text-[11px] text-text-muted">Per-user TOTP enrollment</div></div>
                  </div>
                  <span className={['active', 'profile_pending'].includes(selected.status) ? 'text-xs font-semibold text-emerald-600' : 'text-xs font-semibold text-amber-600'}>
                    {selected.archived ? 'Revoked' : ['active', 'profile_pending'].includes(selected.status) ? 'Enrolled' : selected.status === 'totp_pending' ? 'Pending' : 'Not confirmed'}
                  </span>
                </div>
                <div className="grid gap-3 pt-1 sm:grid-cols-2">
                  <InfoField label="Created" value={displayDate(selected.created_at)} />
                  <InfoField label="Invitation expires" value={displayDate(selected.invitation_expires_at)} />
                </div>
              </div>
            </section>

            <section className="rounded-[1.5rem] border border-border bg-surface/80 p-5 backdrop-blur-xl">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Account controls</div>
              <h2 className="mt-1 text-lg font-semibold text-text-primary">Access state</h2>
              <p className="mt-1 text-xs leading-relaxed text-text-muted">These controls change account state. Capability policy, Worker Lock and approval gates remain separate.</p>
              <div className="mt-5 space-y-3">
                {!selected.archived && ['active', 'locked'].includes(selected.status) && (
                  <button onClick={() => { setArchiveForm({ employment_ended_at: new Date().toISOString().slice(0, 10), departure_reason: 'resignation', departure_note: '' }); setPendingAction('archive') }} className="flex w-full items-center gap-3 rounded-xl border border-slate-500/20 bg-slate-500/5 p-3 text-left transition hover:bg-slate-500/10">
                    <Archive className="h-4 w-4 shrink-0 text-slate-600" />
                    <div className="flex-1"><div className="text-sm font-medium text-text-primary">Archive / mark as leaver</div><div className="text-[11px] text-text-muted">Revoke access immediately and move this person to Former Staff with a governed retention record.</div></div>
                    <ChevronRight className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {selected.archived && (
                  <>
                    <button onClick={() => { setErasureChannel('email'); setErasureScope('all personal data'); setPendingAction('erasure_request') }} className="flex w-full items-center gap-3 rounded-xl border border-blue-500/15 bg-blue-500/5 p-3 text-left transition hover:bg-blue-500/10">
                      <FileClock className="h-4 w-4 shrink-0 text-blue-600" />
                      <div className="flex-1"><div className="text-sm font-medium text-text-primary">Record erasure request</div><div className="text-[11px] text-text-muted">Open a data-erasure review case with OPS's 28-day internal response target.</div></div>
                      <ChevronRight className="h-4 w-4 text-text-muted" />
                    </button>
                    <button onClick={() => { setLegalHoldReason(selected.legal_hold_reason || ''); setPendingAction(selected.legal_hold ? 'legal_hold_release' : 'legal_hold_set') }} className="flex w-full items-center gap-3 rounded-xl border border-amber-500/15 bg-amber-500/5 p-3 text-left transition hover:bg-amber-500/10">
                      <Scale className="h-4 w-4 shrink-0 text-amber-600" />
                      <div className="flex-1"><div className="text-sm font-medium text-text-primary">{selected.legal_hold ? 'Release legal hold' : 'Set legal hold'}</div><div className="text-[11px] text-text-muted">{selected.legal_hold ? 'Release the hold when the legal reason has ended.' : 'Prevent disposal while records are required for a claim, investigation or legal obligation.'}</div></div>
                      <ChevronRight className="h-4 w-4 text-text-muted" />
                    </button>
                  </>
                )}
                {selected.status === 'active' && (
                  <button onClick={() => setPendingAction('lock')} className="flex w-full items-center gap-3 rounded-xl border border-amber-500/15 bg-amber-500/5 p-3 text-left transition hover:bg-amber-500/10">
                    <Lock className="h-4 w-4 shrink-0 text-amber-600" />
                    <div className="flex-1"><div className="text-sm font-medium text-text-primary">Lock account</div><div className="text-[11px] text-text-muted">Temporarily prevent login. Reversible.</div></div>
                    <ChevronRight className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {selected.status === 'locked' && (
                  <button onClick={() => setPendingAction('unlock')} className="flex w-full items-center gap-3 rounded-xl border border-emerald-500/15 bg-emerald-500/5 p-3 text-left transition hover:bg-emerald-500/10">
                    <Unlock className="h-4 w-4 shrink-0 text-emerald-600" />
                    <div className="flex-1"><div className="text-sm font-medium text-text-primary">Unlock account</div><div className="text-[11px] text-text-muted">Restore login access.</div></div>
                    <ChevronRight className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {ONBOARDING.has(selected.status) && (
                  <button onClick={() => { setDeleteConfirm(''); setPendingAction('delete_test') }} className="flex w-full items-center gap-3 rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-left transition hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4 shrink-0 text-red-600" />
                    <div className="flex-1"><div className="text-sm font-medium text-text-primary">Delete test/incomplete account</div><div className="text-[11px] text-text-muted">Permanently remove an abandoned onboarding identity and its login records. Active, locked and revoked identities cannot be deleted.</div></div>
                    <ChevronRight className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {!selected.archived && selected.status !== 'revoked' && (
                  <button onClick={() => setPendingAction('revoke')} className="flex w-full items-center gap-3 rounded-xl border border-red-500/15 bg-red-500/5 p-3 text-left transition hover:bg-red-500/10">
                    <ShieldOff className="h-4 w-4 shrink-0 text-red-500" />
                    <div className="flex-1"><div className="text-sm font-medium text-text-primary">Revoke access</div><div className="text-[11px] text-text-muted">Terminate sessions and revoke the account.</div></div>
                    <ChevronRight className="h-4 w-4 text-text-muted" />
                  </button>
                )}
                {selected.status === 'invited' && (
                  <button onClick={() => setPendingAction('resend_invite')} className="flex w-full items-center gap-3 rounded-xl border border-blue-500/15 bg-blue-500/5 p-3 text-left transition hover:bg-blue-500/10">
                    <Mail className="h-4 w-4 shrink-0 text-blue-500" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-text-primary">Resend invitation</div>
                      <div className="text-[11px] text-text-muted">Send the secure invitation link again and refresh the pending invitation for 24 hours. No OTP, authenticator or profile progress is reset.</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-text-muted" />
                  </button>
                )}
              </div>
            </section>
          </div>
        )}

        {pendingAction && (
          <div className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/45 p-3 backdrop-blur-sm sm:items-center" onClick={() => { if (!actionBusy) { setPendingAction(null); setDeleteConfirm('') } }}>
            <div className="w-full max-w-md rounded-[1.5rem] border border-border bg-surface p-5 shadow-2xl" onClick={event => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Confirm account action</div>
                  <h3 className="mt-1 text-lg font-semibold text-text-primary">
                    {pendingAction === 'delete_test' ? 'Permanently delete this test account?' : pendingAction === 'resend_invite' ? 'Resend this invitation?' : pendingAction === 'archive' ? 'Archive this staff member?' : pendingAction === 'erasure_request' ? 'Record an erasure request?' : pendingAction === 'legal_hold_set' ? 'Set a legal hold?' : pendingAction === 'legal_hold_release' ? 'Release the legal hold?' : pendingAction === 'lock' ? 'Lock this account?' : pendingAction === 'unlock' ? 'Unlock this account?' : 'Revoke this account?'}
                  </h3>
                </div>
                <button onClick={() => { setPendingAction(null); setDeleteConfirm('') }} disabled={actionBusy} className="rounded-lg p-1.5 text-text-muted hover:bg-surface-elevated"><X className="h-4 w-4" /></button>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-text-muted">
                {pendingAction === 'delete_test'
                  ? 'This permanently removes the incomplete Gateway identity plus its OTP, TOTP, session, recovery and MCP identity records. It is only available before activation. Platform audit logs are retained.'
                  : pendingAction === 'resend_invite'
                    ? 'OPS will resend the secure invitation link using the current default template for this role and refresh the pending invitation for 24 hours. No OTP, authenticator or profile progress is reset.'
                    : pendingAction === 'archive'
                      ? 'Archiving is the normal leaver workflow. OPS revokes authentication immediately, moves the person to Former Staff and starts the retention-review lifecycle. It does not erase employment evidence.'
                      : pendingAction === 'erasure_request'
                        ? 'This records a data-subject erasure request for review. It does not automatically delete records because some categories may still need to be retained for legal or regulatory reasons.'
                        : pendingAction === 'legal_hold_set'
                          ? 'A legal hold prevents disposal while records are needed for a claim, investigation or legal obligation.'
                          : pendingAction === 'legal_hold_release'
                            ? 'Release the hold only when the documented legal reason has ended.'
                            : pendingAction === 'revoke'
                              ? 'This terminates active sessions and is intentionally not reversible through the ordinary Staff Portal.'
                              : pendingAction === 'lock'
                                ? 'The person will be unable to log in until an authorised administrator unlocks the account.'
                                : 'The person will regain the ability to authenticate normally.'}
              </p>
              {pendingAction === 'archive' && selected && (
                <div className="mt-4 space-y-3 rounded-xl border border-slate-500/20 bg-slate-500/5 p-3">
                  <label className="block"><span className="text-[11px] font-semibold text-text-primary">Employment ended</span><input type="date" value={archiveForm.employment_ended_at} onChange={event => setArchiveForm(current => ({ ...current, employment_ended_at: event.target.value }))} className="mt-1 w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text-primary" /></label>
                  <label className="block"><span className="text-[11px] font-semibold text-text-primary">Departure reason</span><select value={archiveForm.departure_reason} onChange={event => setArchiveForm(current => ({ ...current, departure_reason: event.target.value }))} className="mt-1 w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text-primary"><option value="resignation">Resignation</option><option value="dismissal">Dismissal</option><option value="redundancy">Redundancy</option><option value="end_of_contract">End of contract</option><option value="retirement">Retirement</option><option value="transfer">Transfer</option><option value="other">Other</option></select></label>
                  <label className="block"><span className="text-[11px] font-semibold text-text-primary">Optional note</span><textarea value={archiveForm.departure_note} onChange={event => setArchiveForm(current => ({ ...current, departure_note: event.target.value }))} maxLength={500} className="mt-1 min-h-20 w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text-primary" /></label>
                </div>
              )}
              {pendingAction === 'erasure_request' && selected && (
                <div className="mt-4 space-y-3 rounded-xl border border-blue-500/15 bg-blue-500/5 p-3">
                  <label className="block"><span className="text-[11px] font-semibold text-text-primary">Request received via</span><select value={erasureChannel} onChange={event => setErasureChannel(event.target.value as typeof erasureChannel)} className="mt-1 w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text-primary"><option value="email">Email</option><option value="letter">Letter</option><option value="portal">Portal</option><option value="verbal">Verbal</option><option value="other">Other</option></select></label>
                  <label className="block"><span className="text-[11px] font-semibold text-text-primary">Scope / wording</span><textarea value={erasureScope} onChange={event => setErasureScope(event.target.value)} maxLength={500} className="mt-1 min-h-20 w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text-primary" /></label>
                </div>
              )}
              {(pendingAction === 'legal_hold_set' || pendingAction === 'legal_hold_release') && selected && (
                <div className="mt-4 rounded-xl border border-amber-500/15 bg-amber-500/5 p-3"><label className="block"><span className="text-[11px] font-semibold text-text-primary">Reason {pendingAction === 'legal_hold_set' ? '(required)' : '(optional)'}</span><textarea value={legalHoldReason} onChange={event => setLegalHoldReason(event.target.value)} maxLength={500} className="mt-1 min-h-20 w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text-primary" /></label></div>
              )}
              {pendingAction === 'delete_test' && selected && (
                <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/5 p-3">
                  <label className="block text-[11px] font-semibold text-text-primary">Type the account email to confirm</label>
                  <div className="mt-1 break-all text-[11px] text-text-muted">{selected.email}</div>
                  <input
                    value={deleteConfirm}
                    onChange={event => setDeleteConfirm(event.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    className="mt-2 w-full rounded-xl border border-red-500/20 bg-bg px-3 py-2 text-sm text-text-primary outline-none focus:border-red-500/50"
                    placeholder="Enter the exact email address"
                  />
                </div>
              )}
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => { setPendingAction(null); setDeleteConfirm('') }} disabled={actionBusy} className="rounded-xl px-4 py-2 text-xs font-medium text-text-muted hover:bg-surface-elevated">Cancel</button>
                <button
                  onClick={() => void runAccountAction(pendingAction)}
                  disabled={actionBusy || (pendingAction === 'delete_test' && deleteConfirm.trim().toLowerCase() !== selected.email.trim().toLowerCase()) || (pendingAction === 'archive' && !archiveForm.employment_ended_at) || (pendingAction === 'legal_hold_set' && !legalHoldReason.trim()) || (pendingAction === 'erasure_request' && !erasureScope.trim())}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold text-white disabled:opacity-50 ${pendingAction === 'revoke' || pendingAction === 'delete_test' ? 'bg-red-500' : pendingAction === 'archive' ? 'bg-slate-600' : 'bg-accent'}`}
                >
                  {actionBusy && <Loader2 className="h-4 w-4 animate-spin" />} {pendingAction === 'delete_test' ? 'Delete permanently' : pendingAction === 'resend_invite' ? 'Resend invitation' : pendingAction === 'archive' ? 'Archive staff member' : pendingAction === 'erasure_request' ? 'Record request' : pendingAction === 'legal_hold_set' ? 'Set legal hold' : pendingAction === 'legal_hold_release' ? 'Release hold' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative min-h-full w-full overflow-hidden bg-[var(--workspace-page-bg)] pb-12">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[34rem] bg-[radial-gradient(circle_at_82%_18%,rgba(59,130,246,0.13),transparent_30%),radial-gradient(circle_at_12%_0%,rgba(14,165,233,0.08),transparent_28%)]" />
      <div className="pointer-events-none absolute -right-28 top-44 h-80 w-80 rounded-full bg-accent/5 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pt-12 sm:px-8 sm:pt-12 lg:px-12">
        <section className="relative pb-10 sm:min-h-[27rem] sm:pb-10">
          <div className="relative z-10 max-w-3xl">
            <div className="mb-7 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent sm:text-xs">
              <Users className="h-4 w-4" /> Staff Portal
            </div>
            <h1 className="max-w-3xl font-serif text-[2.45rem] font-semibold leading-[1.04] tracking-[-0.035em] text-text-primary sm:text-5xl lg:text-6xl">
              Your people, onboarding and access in one place.
            </h1>
            <p className="mt-6 max-w-2xl text-[15px] leading-7 text-text-secondary sm:text-lg sm:leading-8">
              Invite staff, follow onboarding, manage profiles and review each person’s effective access without mixing identity with capability authority.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={() => void openInviteModal()}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-accent px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(37,99,235,0.22)] transition hover:-translate-y-0.5 hover:opacity-95"
              >
                <UserPlus className="h-5 w-5" /> Invite staff
              </button>
              <button
                onClick={() => void Promise.all([loadStaff(true), loadFormerStaff()])}
                disabled={refreshing}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-surface/70 px-5 py-3 text-sm font-semibold text-text-secondary shadow-[0_8px_24px_rgba(15,23,42,0.06)] ring-1 ring-border/60 transition hover:bg-surface hover:text-accent disabled:opacity-50"
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          <div className="pointer-events-none relative ml-auto mt-8 h-52 w-64 opacity-95 sm:absolute sm:right-4 sm:top-14 sm:mt-0 sm:h-80 sm:w-80">
            <div className="absolute inset-3 rounded-[38%] bg-gradient-to-br from-accent/10 via-sky-400/5 to-transparent blur-sm" />
            <div className="absolute right-12 top-8 h-36 w-28 rotate-[5deg] rounded-3xl bg-white/92 shadow-[0_18px_42px_rgba(37,99,235,0.10)] ring-1 ring-white dark:bg-slate-900/90 dark:ring-slate-700/60 backdrop-blur-md">
              <div className="mx-auto mt-6 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent"><UserCheck className="h-6 w-6" /></div>
              <div className="mx-auto mt-5 h-2 w-14 rounded-full bg-accent/10" /><div className="mx-auto mt-2 h-2 w-9 rounded-full bg-accent/5" />
            </div>
            <div className="absolute bottom-8 right-6 h-36 w-28 rotate-[8deg] rounded-3xl bg-white/92 shadow-[0_18px_42px_rgba(37,99,235,0.10)] ring-1 ring-white dark:bg-slate-900/90 dark:ring-slate-700/60 backdrop-blur-md">
              <div className="mx-auto mt-6 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent"><Users className="h-6 w-6" /></div>
              <div className="mx-auto mt-5 h-2 w-14 rounded-full bg-accent/10" /><div className="mx-auto mt-2 h-2 w-10 rounded-full bg-accent/5" />
            </div>
            <div className="absolute bottom-2 left-8 h-40 w-32 -rotate-[7deg] rounded-3xl bg-white shadow-[0_24px_55px_rgba(37,99,235,0.16)] ring-1 ring-white dark:bg-slate-900 dark:ring-slate-700/60 backdrop-blur-lg">
              <div className="mx-auto mt-7 flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent"><UserCheck className="h-7 w-7" /></div>
              <div className="mx-auto mt-5 h-2.5 w-16 rounded-full bg-accent/10" /><div className="mx-auto mt-2 h-2.5 w-11 rounded-full bg-accent/5" />
              <span className="absolute -bottom-2 -right-2 flex h-10 w-10 items-center justify-center rounded-full bg-accent text-white shadow-lg"><CheckCircle2 className="h-5 w-5" /></span>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-5 flex items-start gap-2 rounded-2xl bg-red-500/6 px-4 py-3 text-sm text-red-500 ring-1 ring-red-500/10">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="mb-5 flex items-start gap-2 rounded-2xl bg-emerald-500/6 px-4 py-3 text-sm text-emerald-600 ring-1 ring-emerald-500/10">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {success}
          </div>
        )}

        <section aria-label="Staff summary" className="space-y-3">
          {[
            ['People', 'Total team members', metrics.total, Users, 'all' as DirectoryFilter],
            ['Active', 'Currently active', metrics.active, UserCheck, 'active' as DirectoryFilter],
            ['Onboarding', 'In progress', metrics.onboarding, Clock3, 'onboarding' as DirectoryFilter],
            ['Attention', 'Needs review', metrics.attention, AlertCircle, 'attention' as DirectoryFilter],
            ['Former staff', 'Previously active', metrics.archived, Archive, 'archived' as DirectoryFilter],
          ].map(([label, description, value, Icon, target]) => {
            const MetricIcon = Icon as typeof Users
            const active = filter === target
            const iconTone = target === 'active'
              ? 'bg-emerald-500/10 text-emerald-600'
              : target === 'attention'
                ? 'bg-amber-500/12 text-amber-600'
                : target === 'archived'
                  ? 'bg-slate-500/10 text-slate-500'
                  : 'bg-accent/10 text-accent'
            return (
              <button
                key={label as string}
                onClick={() => setFilter(target as DirectoryFilter)}
                className={`group flex w-full items-center gap-4 rounded-[1.35rem] bg-white/95 px-4 py-4 text-left shadow-[0_10px_28px_rgba(15,23,42,0.055)] transition dark:bg-slate-900/90 sm:px-5 sm:py-5 ${active ? 'ring-1 ring-accent/15' : 'hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(15,23,42,0.075)]'}`}
              >
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconTone}`}><MetricIcon className="h-5 w-5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-text-primary sm:text-base">{label as string}</span>
                  <span className="mt-0.5 block text-xs text-text-muted sm:text-sm">{description as string}</span>
                </span>
                <span className="text-2xl font-semibold tracking-tight text-text-primary sm:text-3xl">{value as number}</span>
                <ChevronRight className={`h-5 w-5 transition group-hover:translate-x-0.5 ${active ? 'text-accent' : 'text-text-muted/45'}`} />
              </button>
            )
          })}
        </section>

        <section className="pt-8 sm:pt-10">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted/70" />
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search people, role, department..."
              className="w-full rounded-[1.35rem] bg-white py-4 pl-12 pr-4 text-sm text-text-primary shadow-[0_10px_28px_rgba(15,23,42,0.055)] outline-none transition placeholder:text-text-muted/45 focus:shadow-[0_12px_30px_rgba(37,99,235,0.10)] dark:bg-slate-900 sm:text-base"
            />
          </div>

          <div className="mt-4 flex gap-1 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {([
              ['all', 'All'], ['active', 'Active'], ['onboarding', 'Onboarding'], ['attention', 'Attention'], ['archived', 'Former'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`whitespace-nowrap rounded-full px-4 py-2.5 text-xs font-medium transition sm:px-5 sm:text-sm ${
                  filter === id ? 'bg-accent/10 text-accent' : 'text-text-muted hover:bg-surface/60 hover:text-text-primary'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <section className="pt-5">
          {loading ? (
            <div className="flex min-h-56 items-center justify-center">
              <div className="text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" />
                <p className="mt-3 text-sm text-text-muted">Loading your staff directory…</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex min-h-56 items-center justify-center text-center">
              <div>
                <Users className="mx-auto h-8 w-8 text-text-muted/35" />
                <p className="mt-3 text-sm font-semibold text-text-primary">No staff match this view.</p>
                <p className="mt-1 text-xs text-text-muted">Try another filter or search term.</p>
              </div>
            </div>
          ) : (
            <div className="border-t border-border/50">
              {filtered.map(member => (
                <button
                  key={member.user_id}
                  onClick={() => void openMember(member)}
                  className="group flex w-full items-center gap-3 border-b border-border/50 px-1 py-4 text-left transition hover:bg-surface/45 sm:px-2 sm:py-5"
                >
                  <Avatar member={member} size={48} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-text-primary sm:text-base">{member.display_name || member.email.split('@')[0]}</div>
                    <div className="mt-0.5 truncate text-xs text-text-muted sm:text-sm">
                      {member.archived ? `Left ${displayDate(member.employment_ended_at)}${member.department ? ` · ${member.department}` : ''}` : `${member.job_title || member.email}${member.department ? ` · ${member.department}` : ''}`}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <StatusBadge status={member.status} />
                      <RoleBadge role={member.role} />
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-text-muted/40 transition group-hover:translate-x-0.5 group-hover:text-accent" />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {showInvite && (
        <div className="fixed inset-0 z-[10050] flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={() => !inviting && setShowInvite(false)}>
          <div className="max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-t-[1.75rem] border border-border bg-surface p-5 shadow-2xl sm:rounded-[1.75rem] sm:p-6" onClick={event => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">New staff member</div>
                <h2 className="mt-1 text-xl font-semibold text-text-primary">Invite someone to OPS</h2>
                <p className="mt-1 text-xs leading-relaxed text-text-muted">They’ll verify their email, enrol their own authenticator and complete onboarding.</p>
              </div>
              <button onClick={() => setShowInvite(false)} disabled={inviting} className="rounded-xl p-2 text-text-muted hover:bg-surface-elevated"><X className="h-4 w-4" /></button>
            </div>

            <div className="mt-5 rounded-2xl border border-accent/12 bg-accent/5 p-4">
              <label className="block">
                <span className="text-xs font-semibold text-text-primary">Account role</span>
                <select
                  value={inviteForm.role}
                  onChange={event => {
                    const role = event.target.value as InviteRole
                    setInviteForm(current => ({
                      ...current,
                      role,
                      template_id: inviteTemplates?.defaults_by_role[role] || '',
                    }))
                  }}
                  disabled={inviting}
                  className="mt-2 w-full rounded-xl border border-border bg-bg/60 px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent/35"
                >
                  {(Object.keys(ROLE_COPY) as InviteRole[]).map(role => <option key={role} value={role}>{ROLE_COPY[role].label}</option>)}
                </select>
              </label>
              <p className="mt-2 text-[11px] leading-relaxed text-text-muted">
                {ROLE_COPY[inviteForm.role].description} Effective capability authority is verified separately after onboarding.
              </p>
            </div>

            <section className="mt-5">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-text-primary">
                    <Palette className="h-4 w-4 text-accent" /> Invitation template
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-text-muted">
                    Choose a professional design. OPS will build the final email dynamically from the recipient, role and details below.
                  </p>
                </div>
                {inviteTemplatesLoading && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
              </div>

              {compatibleInviteTemplates.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  {compatibleInviteTemplates.map(template => {
                    const active = inviteForm.template_id === template.id
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setInviteForm(current => ({ ...current, template_id: template.id }))}
                        disabled={inviting}
                        className={`relative overflow-hidden rounded-2xl border p-3 text-left transition hover:-translate-y-0.5 hover:shadow-sm ${
                          active ? 'border-accent/35 ring-1 ring-accent/15' : 'border-border'
                        }`}
                        style={{ background: template.surface }}
                      >
                        <span className="absolute inset-x-0 top-0 h-1" style={{ background: template.accent }} />
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className="text-xs font-semibold text-slate-900">{template.name}</span>
                          <span className="rounded-full bg-white/70 px-2 py-0.5 text-[9px] font-semibold text-slate-600">{template.tone}</span>
                        </div>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.1em] text-slate-500">{template.eyebrow}</div>
                        <p className="mt-2 text-[10px] leading-relaxed text-slate-600">{template.description}</p>
                        {active && <div className="mt-2 text-[10px] font-semibold text-accent">Selected</div>}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-bg/35 p-3 text-[11px] text-text-muted">
                  {inviteTemplatesLoading ? 'Loading invitation templates…' : 'Role default template will be used.'}
                </div>
              )}

              {selectedInviteTemplate && (
                <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-bg/35">
                  <div className="px-4 py-3" style={{ borderLeft: `4px solid ${selectedInviteTemplate.accent}` }}>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">Dynamic email preview</div>
                    <div className="mt-1 text-sm font-semibold text-text-primary">
                      {inviteForm.first_name.trim() || inviteForm.display_name.trim() || 'Recipient'}, you’re invited to OPS as {ROLE_COPY[inviteForm.role].label}
                    </div>
                    <div className="mt-1 text-[11px] text-text-muted">
                      {inviteForm.job_title.trim() || 'Role details'}{inviteForm.department.trim() ? ` · ${inviteForm.department.trim()}` : ''}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {([
                ['first_name', 'First name', 'Jane'], ['last_name', 'Last name', 'Smith'],
                ['display_name', 'Display name', 'Jane Smith'], ['email', 'Email address', 'jane@company.com'],
                ['job_title', 'Job title', 'Marketing Manager'], ['department', 'Department', 'Marketing'],
                ['phone', 'Phone', '+44 7700 900000'], ['timezone', 'Timezone', 'Europe/London'],
              ] as const).map(([key, label, placeholder]) => (
                <label key={key} className={key === 'email' ? 'sm:col-span-2' : ''}>
                  <span className="mb-1 block text-[11px] font-medium text-text-muted">{label}{key === 'email' ? ' *' : ''}</span>
                  <input
                    type={key === 'email' ? 'email' : 'text'}
                    value={inviteForm[key]}
                    onChange={event => setInviteForm(current => ({ ...current, [key]: event.target.value }))}
                    placeholder={placeholder}
                    disabled={inviting}
                    className="w-full rounded-xl border border-border bg-bg/55 px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted/35 focus:border-accent/35"
                  />
                </label>
              ))}
              <label>
                <span className="mb-1 block text-[11px] font-medium text-text-muted">Locale</span>
                <input
                  value={inviteForm.locale}
                  onChange={event => setInviteForm(current => ({ ...current, locale: event.target.value }))}
                  disabled={inviting}
                  className="w-full rounded-xl border border-border bg-bg/55 px-3 py-2.5 text-sm text-text-primary outline-none transition focus:border-accent/35"
                />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-1 flex items-center gap-2 text-[11px] font-medium text-text-muted">
                <MessageSquare className="h-3.5 w-3.5" /> Personal message <span className="font-normal">(optional)</span>
              </span>
              <textarea
                value={inviteForm.personal_message}
                onChange={event => setInviteForm(current => ({ ...current, personal_message: event.target.value.slice(0, 800) }))}
                placeholder="Add a short personal welcome or context for this invitation..."
                rows={4}
                disabled={inviting}
                className="w-full resize-none rounded-xl border border-border bg-bg/55 px-3 py-2.5 text-sm text-text-primary outline-none transition placeholder:text-text-muted/35 focus:border-accent/35"
              />
              <div className="mt-1 text-right text-[10px] text-text-muted">{inviteForm.personal_message.length}/800</div>
            </label>

            <div className="mt-6 flex flex-col-reverse gap-2 border-t border-border/60 pt-4 sm:flex-row sm:justify-end">
              <button onClick={() => setShowInvite(false)} disabled={inviting} className="rounded-xl px-4 py-2.5 text-xs font-medium text-text-muted hover:bg-surface-elevated">Cancel</button>
              <button
                onClick={() => void inviteStaff()}
                disabled={inviting || !inviteForm.email.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
              >
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
